import React, { useEffect, useState } from "react";
import {
  fetchPlatformSettings,
  updatePlatformSettings,
} from "../../services/platformSettingsApi";
import "../../styles/admin-common.css";

const emptyForm = {
  legalName: "",
  addressLines: "",
  gstin: "",
  gstPercent: "18",
  stateName: "Tamil Nadu",
  stateCode: "33",
  supportEmail: "",
  websiteUrl: "",
  supportPhone: "",
};

const toPercentInput = (rate) => {
  const value = Number(rate ?? 0);
  if (!Number.isFinite(value)) return "18";
  return String(value <= 1 ? value * 100 : value);
};

/** Admin place to update SpotDeal company details used on invoices (GST, GSTIN, address). */
const PlatformSettings = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPlatformSettings();
      setForm({
        legalName: data.legalName || "",
        addressLines: data.addressLines || "",
        gstin: data.gstin || "",
        gstPercent: toPercentInput(data.gstPercent),
        stateName: data.stateName || "Tamil Nadu",
        stateCode: data.stateCode || "33",
        supportEmail: data.supportEmail || "",
        websiteUrl: data.websiteUrl || "",
        supportPhone: data.supportPhone || "",
      });
      setUpdatedAt(data.updatedAt || null);
    } catch (e) {
      setError(e.message || "Could not load SpotDeal details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setOk("");
    try {
      const data = await updatePlatformSettings({
        legalName: form.legalName.trim(),
        addressLines: form.addressLines.trim(),
        gstin: form.gstin.trim() || undefined,
        gstPercent: parseFloat(form.gstPercent),
        stateName: form.stateName.trim(),
        stateCode: form.stateCode.trim(),
        supportEmail: form.supportEmail.trim() || undefined,
        websiteUrl: form.websiteUrl.trim() || undefined,
        supportPhone: form.supportPhone.trim() || undefined,
      });
      setForm({
        legalName: data.legalName || "",
        addressLines: data.addressLines || "",
        gstin: data.gstin || "",
        gstPercent: toPercentInput(data.gstPercent),
        stateName: data.stateName || "",
        stateCode: data.stateCode || "",
        supportEmail: data.supportEmail || "",
        websiteUrl: data.websiteUrl || "",
        supportPhone: data.supportPhone || "",
      });
      setUpdatedAt(data.updatedAt || null);
      setOk("SpotDeal details saved. New invoices will use these GST and company values.");
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <h2>SpotDeal details</h2>
      <p className="subtitle">
        Company legal name, address, GSTIN and GST % used on vendor commission invoices and wallet
        receipts. Region commission / convenience fee stay under <strong>Regions</strong>.
      </p>

      {error && <p style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</p>}
      {ok && <p style={{ color: "#15803d", marginBottom: 12 }}>{ok}</p>}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <form className="admin-card admin-form" onSubmit={onSubmit} style={{ maxWidth: 720 }}>
          {updatedAt && (
            <p className="muted-text" style={{ marginTop: 0 }}>
              Last updated: {new Date(updatedAt).toLocaleString()}
            </p>
          )}

          <label className="field-label">
            <span>Legal name</span>
            <input value={form.legalName} onChange={onChange("legalName")} required maxLength={200} />
          </label>

          <label className="field-label">
            <span>Registered address (one line per row)</span>
            <textarea rows={5} value={form.addressLines} onChange={onChange("addressLines")} required />
          </label>

          <label className="field-label">
            <span>GSTIN</span>
            <input
              value={form.gstin}
              onChange={onChange("gstin")}
              maxLength={30}
              placeholder="e.g. 33AAAAA0000A1Z5"
            />
          </label>

          <label className="field-label">
            <span>GST % on platform commission (shown on invoices)</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.gstPercent}
              onChange={onChange("gstPercent")}
              required
            />
            <span className="muted-text" style={{ fontSize: 12 }}>
              Enter 18 for 18%. Commission on invoices is treated as GST-inclusive at this rate.
            </span>
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 12 }}>
            <label className="field-label">
              <span>State</span>
              <input value={form.stateName} onChange={onChange("stateName")} required maxLength={100} />
            </label>
            <label className="field-label">
              <span>State code</span>
              <input value={form.stateCode} onChange={onChange("stateCode")} required maxLength={10} />
            </label>
          </div>

          <label className="field-label">
            <span>Support email</span>
            <input value={form.supportEmail} onChange={onChange("supportEmail")} maxLength={120} />
          </label>

          <label className="field-label">
            <span>Website</span>
            <input value={form.websiteUrl} onChange={onChange("websiteUrl")} maxLength={200} />
          </label>

          <label className="field-label">
            <span>Support phone</span>
            <input value={form.supportPhone} onChange={onChange("supportPhone")} maxLength={30} />
          </label>

          <div className="form-actions">
            <button type="submit" className="admin-btn" disabled={saving}>
              {saving ? "Saving…" : "Save SpotDeal details"}
            </button>
            <button type="button" className="admin-btn secondary" onClick={load} disabled={saving}>
              Reload
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PlatformSettings;
